import { useStore } from "@/store";
import { computed } from "vue";
import { LanguageTypes } from "@/core/types/LanguageTypes";
import { isIsecDomain, isBblDomain } from "./domainList";

export enum TenantTypes {
  au = 1,
  bvi = 10000,
  sea = 10004,
  jp = 10005,
  bbl = 10006,
}

export const TenantOptions = [
  { label: "BVI", value: TenantTypes.bvi },
  { label: "SEA", value: TenantTypes.sea },
  { label: "Australia", value: TenantTypes.au },
  { label: "Japan", value: TenantTypes.jp },
  { label: "BBL", value: TenantTypes.bbl },
];

export enum tenancies {
  au = "au",
  bvi = "bvi",
  sea = "sea",
  jp = "jp",
  bbl = "bbl",
}

export const websiteTenant = [
  { label: "BVI", value: "bvi" },
  { label: "SEA", value: "vn" },
  { label: "Japan", value: "jp" },
  { label: "AU", value: "ba" },
  { label: "CN", value: "cn" },
  { label: "TW", value: "tw" },
  { label: "BBL", value: "bbl" },
];

export const getTenancy = computed(() => {
  if (isIsecDomain()) {
    return tenancies.jp;
  }
  if (isBblDomain()) {
    return tenancies.bbl;
  }

  let site = process.env.VUE_APP_SITE;
  if (tenancies[site] === undefined) {
    site = tenancies.bvi;
  }
  return site;
});

export const getUserTenancy = () => {
  const store = useStore();
  const user = computed(() => store.state.AuthModule?.user);
  return user?.value.tenancy;
};

export const getUserTenantId = () => {
  const label = getTenancyByLocalStorage();
  return TenantTypes[label];
};

export const getTenancyByLocalStorage = () => {
  const user = localStorage.getItem("user");
  if (user) {
    return JSON.parse(user)["tenancy"];
  }
  return null;
};

export const getVerificationTenancy = () => {
  if (isIsecDomain()) {
    return tenancies.jp;
  }

  const userTenancy = getUserTenancy();
  if (userTenancy && userTenancy != tenancies.jp) {
    return userTenancy;
  }

  let site = process.env.VUE_APP_SITE;
  if (tenancies[site] === undefined) {
    site = tenancies.bvi;
  }
  return site;
};

export enum tenantNames {
  bvi = "BCR",
  jp = "I Securities",
}

export const getTenantName = computed(() => {
  const tenancy = getTenancy.value;
  return tenantNames[tenancy];
});

export const canLiveChat = computed(() => {
  const tenancy = getTenancy.value;
  const canLiveChatList = [tenancies.bvi];
  return canLiveChatList.includes(tenancy);
});

export const getTenantFavicon = computed(() => {
  const tenancy = getTenancy.value;
  const tenantFavicon = {
    [tenancies.bvi]: "/favicon.ico",
    [tenancies.jp]: "/isec-favicon.ico",
  };
  return tenantFavicon[tenancy] || tenantFavicon[tenancies.bvi];
});

export const setFavicon = () => {
  let link: HTMLLinkElement | null =
    document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = getTenantFavicon.value;
};

export const getTenantLogo = computed(() => {
  const tenancy = getTenancy.value;
  const tenantLogo = {
    [tenancies.bvi]: {
      src: "/images/logos/logo.svg",
      style: {
        width: "140px",
      },
    },
    [tenancies.au]: {
      src: "/images/logos/logo.svg",
      style: {
        width: "140px",
      },
    },
    [tenancies.sea]: {
      src: "/images/logos/logo.svg",
      style: {
        width: "140px",
      },
    },
    [tenancies.jp]: {
      src: "/images/logos/isec-logo.png",
      style: {
        width: "150px",
      },
    },
  };
  if (tenantLogo[tenancy] == undefined) {
    return {
      src: "/images/logos/logo.svg",
      style: {
        width: "140px",
      },
    };
  }
  return tenantLogo[tenancy] || tenantLogo[tenancies.bvi];
});

export const getBackendTenantLogo = computed(() => {
  const tenancy = getTenancy.value;
  const tenantLogo = {
    [tenancies.bvi]: {
      src: "/images/logos/logo-black.svg",
      class: "h-40px app-sidebar-logo-default",
    },
    [tenancies.jp]: {
      src: "/images/logos/isec-logo.png",
      class: "h-30px app-sidebar-logo-default",
    },
  };
  return tenantLogo[tenancy] || tenantLogo[tenancies.bvi];
});

export const getBackendMiniLogo = computed(() => {
  const tenancy = getTenancy.value;
  const tenantLogo = {
    [tenancies.bvi]: {
      src: "/images/logos/default-small.svg",
      class: "h-20px app-sidebar-logo-minimize",
    },
    [tenancies.jp]: {
      src: "/images/logos/jp-logo-sm.jpg",
      class: "h-30px app-sidebar-logo-minimize",
    },
  };
  return tenantLogo[tenancy] || tenantLogo[tenancies.bvi];
});

export const getTenantWalletLogo = computed(() => {
  const tenancy = getTenancy.value;
  const tenantWalletLogo = {
    [tenancies.bvi]: {
      src: "/images/logos/logo-wallet-bg.png",
      style: {},
    },
    [tenancies.jp]: {
      src: "/images/logos/isec-logo.png",
      style: {
        width: "100px",
        height: "auto",
      },
    },
  };

  // Return specific tenant logo, or default to BVI if not found
  return tenantWalletLogo[tenancy] || tenantWalletLogo[tenancies.bvi];
});

export const getTenantLanguagesOptions = computed(() => {
  const tenancy = getTenancy.value;
  const tenantLanguagesOptions = {
    [tenancies.bvi]: LanguageTypes.all,
    [tenancies.au]: LanguageTypes.all,
    [tenancies.jp]: [
      LanguageTypes.enUS,
      LanguageTypes.jpJP,
      LanguageTypes.zhCN,
      LanguageTypes.zhTW,
    ],
    [tenancies.bbl]: LanguageTypes.all,
  };
  return tenantLanguagesOptions[tenancy] || LanguageTypes.all;
});

export const getBackendFooterName = computed(() => {
  const tenancy = getTenancy.value;
  switch (tenancy) {
    case tenancies.jp:
      return "ISec";
    default:
      return "BCR Pro";
  }
});
